import tailSelect from 'tail.select.js'
import { serviceInfoPopup } from './handle-actual-offers'
import marketService from 'services/market-service'
import { pushDataLayer } from 'utils/data-layer-push'
import { storageService } from 'services/storage'
import { formatNumber } from 'utils/format-number'
import { t9n } from 'src/assets/scripts/t9n/t9n'
import { fetchSupportedCurrencyPairs } from 'src/assets/scripts/services/settings'

declare const USER_BASE_URL_WT
declare let SINGLE_RATE_BASE_CURRENCY: string
declare let SINGLE_RATE_COUNTER_CURRENCY: string
let timeout

type TransactionDirection = 'buy' | 'sell'

interface SharedElements {
  sellTailSelect: tailSelect
  buyTailSelect: tailSelect
  sellElementValue: string
  buyElementValue: string
  sellCurrency: string
  buyCurrency: string
  amountInput?: HTMLInputElement
  isRedirectPairExist?: boolean
  transactionDirection: string
}

interface CurrencyID {
  firstInput: string
  secondInput: string
}

interface SupportedCurrencyPairs {
  supportedPair: string
  secondCombination: string
}

interface PairToRedirect {
  redirectPair: string | boolean
  shouldChangePage: boolean
  isSecondCombination: boolean
}

interface UserSettingStorage {
  userCurrencyValue: string
  userFieldDirection: string
  shouldSwapCurrencies: boolean
}

const userSettingStorage: UserSettingStorage = {
  userCurrencyValue: '1000',
  userFieldDirection: 'rateBuyInput',
  shouldSwapCurrencies: false,
}

const inputCurrencyIDHolder: CurrencyID = {
  firstInput: '',
  secondInput: '',
}

const setLocalStorage = (name: string, value: string | boolean): void => {
  userSettingStorage[name] = value
  const toStore = JSON.stringify({ ...userSettingStorage })
  storageService.setItem('singleRateCalculator', toStore)
}

const getLocalStorage = (): void => {
  const storage = storageService.getItem('singleRateCalculator')
  if (storage) Object.assign(userSettingStorage, JSON.parse(storage))
}
getLocalStorage()

const isRateInputSell = userSettingStorage['userFieldDirection'] === 'rateSellInput'

const sharedElementsHolder: SharedElements = {
  sellTailSelect: null,
  buyTailSelect: null,
  sellElementValue: '',
  buyElementValue: '',
  sellCurrency: isRateInputSell ? SINGLE_RATE_COUNTER_CURRENCY : SINGLE_RATE_BASE_CURRENCY,
  buyCurrency: isRateInputSell ? SINGLE_RATE_BASE_CURRENCY : SINGLE_RATE_COUNTER_CURRENCY,
  amountInput: null,
  isRedirectPairExist: true,
  transactionDirection: isRateInputSell ? 'buy' : 'sell',
}

const setOption = (tailDataAttribute: string, anotherSelectValue: string): void => {
  const select = document.querySelector<HTMLSelectElement>(`[data-tail-select="${tailDataAttribute}"]`)

  const sellOptions = Array.from<HTMLOptionElement>(select.options)
  sellOptions.forEach((option) => {
    option.hasAttribute('selected') ? option.removeAttribute('selected') : ''
  })
  const optionToSetAsValue = sellOptions.find((item) => item.text === anotherSelectValue)
  optionToSetAsValue.selected = true
}

const swapSelects = (): void => {
  const sellSelectValue = getSelect(true).value
  const buySelectValue = getSelect(false).value

  setOption('tail-1', buySelectValue)
  setOption('tail-2', sellSelectValue)

  sharedElementsHolder.buyTailSelect.reload()
  sharedElementsHolder.sellTailSelect.reload()

  initSelectService()
}

const getSelect = (isFirst: boolean): HTMLSelectElement => {
  const $sellSelect = document.querySelector<HTMLSelectElement>('[data-tail-select="tail-1"]')
  const $buySelect = document.querySelector<HTMLSelectElement>('[data-tail-select="tail-2"]')
  return isFirst ? $sellSelect : $buySelect
}

const createFlagElement = (selector: string): void => {
  const $flagElemnts = document.querySelectorAll<HTMLElement>(selector)

  if ($flagElemnts) {
    $flagElemnts.forEach((el) => {
      el.querySelector('.currency-rate-calculator__flag')?.remove()
      const $flag = document.createElement('span')
      $flag.classList.add(
        'currency-rate-calculator__flag',
        `icon-currency-round-${el.textContent}`,
        `icon-currency-round-${el.textContent}-dims`,
      )
      el.prepend($flag)
    })
  }
}

const setCurrencyFlags = (): void => {
  createFlagElement('.select-label')
  createFlagElement('.tail-select.currency-rate-calculator__select .dropdown-option')
}

const swapCurrencyIDService = (
  elementValue: string,
  buyTailSelectElement: tailSelect,
  tailDataAttribute: string,
): void => {
  setOption(tailDataAttribute, elementValue)
  buyTailSelectElement.reload()
  swapCurrencies(true)
  initSelectService()
}

const initTailSelect = (): void => {
  const allSelects = document.querySelectorAll<HTMLSelectElement>('.currency-rate-calculator__select')

  sharedElementsHolder.sellTailSelect = tailSelect(allSelects[0], {
    classNames: 'currency-rate-calculator__select',
    width: '100',
  }).on('change', (item) => {
    if (item.value === getSelect(false).value) {
      swapCurrencyIDService(sharedElementsHolder.sellElementValue, sharedElementsHolder.buyTailSelect, 'tail-2')
    }
    const $userField = document.querySelector<HTMLInputElement>('[data-user-field]')
    const userFieldDirection = $userField.getAttribute('data-amount') === 'sell' ? 'rateBuyInput' : 'rateSellInput'
    setLocalStorage('userCurrencyValue', $userField.value)
    setLocalStorage('userFieldDirection', userFieldDirection)
    setCurrencyFlags()
  })

  sharedElementsHolder.buyTailSelect = tailSelect(allSelects[1], {
    classNames: 'currency-rate-calculator__select',
    width: '100',
  }).on('change', (item) => {
    if (item.value === getSelect(true).value) {
      swapCurrencyIDService(sharedElementsHolder.buyElementValue, sharedElementsHolder.sellTailSelect, 'tail-1')
    }
    const $userField = document.querySelector<HTMLInputElement>('[data-user-field]')
    const userFieldDirection = $userField.getAttribute('data-amount') === 'sell' ? 'rateBuyInput' : 'rateSellInput'
    setLocalStorage('userCurrencyValue', $userField.value)
    setLocalStorage('userFieldDirection', userFieldDirection)
    setCurrencyFlags()
  })

  setCurrencyFlags()
}

const getActualInputCurrency = (): void => {
  inputCurrencyIDHolder.firstInput = document.querySelector<HTMLSelectElement>('[data-tail-select="tail-1"]').value
  inputCurrencyIDHolder.secondInput = document.querySelector<HTMLSelectElement>('[data-tail-select="tail-2"]').value

  const $calculatorInputs = document.querySelectorAll<HTMLDivElement>('.currency-rate-calculator__input-with-dropdown')
  const $calculatorSellInput = $calculatorInputs[0]
  const $calculatorBuyInput = $calculatorInputs[1]

  const sellSelectElement = $calculatorSellInput.getElementsByTagName('select')[0]
  $calculatorSellInput.addEventListener('click', () => {
    sharedElementsHolder.sellElementValue = sellSelectElement.value
  })

  const buySelectElement = $calculatorBuyInput.getElementsByTagName('select')[0]
  $calculatorBuyInput.addEventListener('click', () => {
    sharedElementsHolder.buyElementValue = buySelectElement.value
  })
}

const initGoogleAnalytics = (): void => {
  clearTimeout(timeout)
  timeout = setTimeout(() => {
    const text = document.querySelector<HTMLElement>('.current-menu-item.main-desktop-menu__item').innerText as string
    pushDataLayer('currency_caluclator', { click_text: text })
  }, 1000)
}

const resetCurrencySwap = (): void => {
  const $redirectMenuItem = document.querySelectorAll<HTMLElement>('.main-desktop-menu__item')
  $redirectMenuItem.forEach((el) => {
    el.addEventListener('click', () => {
      setLocalStorage('shouldSwapCurrencies', false)
      setLocalStorage('userFieldDirection', 'rateBuyInput')
    })
  })
}
resetCurrencySwap()

const handleExchangeButton = (): void => {
  const exchangeButton = document.querySelector<HTMLButtonElement>('.currency-rate-calculator__cta-exchange')
  const exnchangeOptions = document.querySelector<HTMLDivElement>('.currency-rate-calculator__continuing-options')

  exchangeButton.addEventListener('click', (): void => {
    exnchangeOptions.hidden = !exnchangeOptions.hidden
  })

  window.addEventListener('click', (e): void => {
    const target = e?.target as HTMLElement
    if (
      exnchangeOptions.hidden === false &&
      e.target !== exchangeButton &&
      !target.closest('.currency-rate-calculator__cta-exchange') &&
      e.target !== exnchangeOptions &&
      !target.closest('.currency-rate-calculator__continuing-options')
    )
      exnchangeOptions.hidden = true
  })
}

const removeUserAttribute = (): void => {
  const $userInputs = document.querySelectorAll<HTMLInputElement>('[data-user-field]')
  $userInputs.forEach((input) => {
    input.removeAttribute('data-user-field')
  })
}

const $rateSellInput = document.querySelector<HTMLInputElement>('[data-amount="sell"]')
const $rateBuyInput = document.querySelector<HTMLInputElement>('[data-amount="buy"]')
const initInputs = (): void => {
  if ($rateBuyInput && $rateSellInput) {
    const startValue = userSettingStorage['userCurrencyValue']
    const $userStartInput = isRateInputSell ? $rateBuyInput : $rateSellInput
    const $valueStartInput = isRateInputSell ? $rateSellInput : $rateBuyInput
    removeUserAttribute()
    isRateInputSell
      ? $rateBuyInput.setAttribute('data-user-field', '')
      : $rateSellInput.setAttribute('data-user-field', '')

    $userStartInput.value = startValue
    sharedElementsHolder.amountInput = $valueStartInput
  }
}

export const rateInputValidation = (value: string, decimal: number): string => {
  const transformedInput: string[] = value
    .toString()
    .replace(',', '.')
    .replace(/[^0-9.0-9{2}]+/g, '')
    .split('.')
    .slice(0, 2)

  if (transformedInput.length > 1 && !transformedInput[0]) {
    transformedInput[0] = '0'
  }

  if (transformedInput[1]) {
    transformedInput[1] = transformedInput[1].substring(0, decimal)
  }

  if (transformedInput[0].startsWith('0') && transformedInput[0].length > 1) {
    transformedInput[0] = transformedInput[0].slice(1)
  }

  return transformedInput.join(',')
}

const getValueFromInput = (): void => {
  const sellDirection: TransactionDirection = 'sell'
  const buyDirection: TransactionDirection = 'buy'

  $rateSellInput.addEventListener('focus', () => {
    removeUserAttribute()
    $rateSellInput.setAttribute('data-user-field', '')
  })

  $rateBuyInput.addEventListener('focus', () => {
    removeUserAttribute()
    $rateBuyInput.setAttribute('data-user-field', '')
  })

  $rateSellInput.addEventListener('input', () => {
    $rateSellInput.value = rateInputValidation($rateSellInput.value, 2)
    setLocalStorage('userCurrencyValue', $rateSellInput.value)
    sharedElementsHolder.transactionDirection = sellDirection

    askForRate(
      $rateSellInput.value,
      $rateBuyInput,
      inputCurrencyIDHolder.firstInput,
      inputCurrencyIDHolder.secondInput,
      sellDirection,
    )

    initGoogleAnalytics()
  })

  $rateBuyInput.addEventListener('input', () => {
    $rateBuyInput.value = rateInputValidation($rateBuyInput.value, 2)
    setLocalStorage('userCurrencyValue', $rateBuyInput.value)
    sharedElementsHolder.transactionDirection = buyDirection

    askForRate(
      $rateBuyInput.value,
      $rateSellInput,
      inputCurrencyIDHolder.secondInput,
      inputCurrencyIDHolder.firstInput,
      buyDirection,
    )

    initGoogleAnalytics()
  })
  setCurrencyFlags()
}

const hasInputValue = (inputValue: string) => {
  if (sharedElementsHolder.isRedirectPairExist && Number(inputValue.replace(',', '.')) >= 0.01) {
    return true
  }
  return false
}

const askForRate = (
  inputValue: string,
  $inputElement: HTMLInputElement,
  sellCurrency: string,
  buyCurrency: string,
  transactionDirection: string,
) => {
  if (hasInputValue(inputValue)) {
    marketService
      .fetchMarketEstimate(USER_BASE_URL_WT, transactionDirection, inputValue, sellCurrency, buyCurrency)
      .then((response) => {
        $inputElement.value = formatNumber(response.exchange_offers[0].amount, 2)
        sharedElementsHolder.amountInput = $inputElement
        sharedElementsHolder.sellCurrency = sellCurrency
        sharedElementsHolder.buyCurrency = buyCurrency
        sharedElementsHolder.transactionDirection = transactionDirection
      })
  }
}

export const handleUpdateValues = (): void => {
  if ($rateSellInput && $rateBuyInput) {
    askForRate(
      userSettingStorage['userCurrencyValue'],
      sharedElementsHolder.amountInput,
      sharedElementsHolder.sellCurrency,
      sharedElementsHolder.buyCurrency,
      sharedElementsHolder.transactionDirection,
    )
  }
}

const swapCurrencies = (swapAttribues: boolean): void => {
  const $inputFields = document.querySelectorAll<HTMLInputElement>('[data-amount]')
  const actualUserInputValue = document.querySelector<HTMLInputElement>('[data-user-field]').value
  let attributeTransactionDirection = ''

  $inputFields.forEach((singleInputField) => {
    if (singleInputField.hasAttribute('data-user-field')) {
      swapAttribues ? singleInputField.removeAttribute('data-user-field') : ''
    } else {
      swapAttribues ? singleInputField.setAttribute('data-user-field', '') : ''
      attributeTransactionDirection = singleInputField.getAttribute('data-amount')
      singleInputField.value = actualUserInputValue
    }
  })

  let directionToGetInput = ''
  const valueToAsk = document.querySelector<HTMLInputElement>('[data-user-field]')
  if (swapAttribues) {
    directionToGetInput = attributeTransactionDirection === 'sell' ? 'buy' : 'sell'
  } else {
    directionToGetInput = isRateInputSell ? 'sell' : 'buy'
    attributeTransactionDirection = valueToAsk.getAttribute('data-amount')
    const buyCurrency = sharedElementsHolder.buyCurrency
    sharedElementsHolder.buyCurrency = sharedElementsHolder.sellCurrency
    sharedElementsHolder.sellCurrency = buyCurrency
  }
  const $amountInput = document.querySelector<HTMLInputElement>(`[data-amount=${directionToGetInput}]`)
  sharedElementsHolder.amountInput = $amountInput

  inputCurrencyIDHolder.firstInput = document.querySelector<HTMLSelectElement>('[data-tail-select="tail-1"]').value
  inputCurrencyIDHolder.secondInput = document.querySelector<HTMLSelectElement>('[data-tail-select="tail-2"]').value

  askForRate(
    valueToAsk.value,
    $amountInput,
    sharedElementsHolder.sellCurrency,
    sharedElementsHolder.buyCurrency,
    attributeTransactionDirection,
  )
  setCurrencyFlags()
}

const rotateIconService = (): void => {
  const $rotateIcon = document.querySelector<HTMLInputElement>('[data-rotate-icon]')
  $rotateIcon.addEventListener('click', () => {
    $rotateIcon.classList.toggle('is-rotated')
    swapSelects()
    swapCurrencies(true)
  })
}

const showError = (isInfoShown: boolean): void => {
  const errorInfo = document.querySelector<HTMLParagraphElement>('.currency-rate-calculator__error')
  isInfoShown ? (errorInfo.hidden = false) : (errorInfo.hidden = true)
}

const prepareDataForRedirect = (supportedPairs: string[]): SupportedCurrencyPairs[] =>
  [...supportedPairs].map((singlePair) => {
    const firstCurrency = singlePair.slice(0, 3).toLowerCase()
    const secondCurrency = singlePair.slice(3).toLowerCase()
    return {
      supportedPair: `${firstCurrency}-${secondCurrency}`,
      secondCombination: `${secondCurrency}-${firstCurrency}`,
    }
  })

const getSupportedPair = async (redirectPair: string, actualTemplatePair: string): Promise<PairToRedirect> => {
  const supportedPairs = await fetchSupportedCurrencyPairs()
  const supportedCurrencyPairs = supportedPairs
    ? prepareDataForRedirect(supportedPairs['market-currency-pairs'].pairs)
    : []
  const actualPairOptions = supportedCurrencyPairs.find(
    (currencyPair) => currencyPair.supportedPair === redirectPair || currencyPair.secondCombination === redirectPair,
  )

  let shouldChangePage = false
  let isSecondCombination = false
  if (actualPairOptions) {
    shouldChangePage =
      actualPairOptions.supportedPair !== actualTemplatePair &&
      actualPairOptions.secondCombination !== actualTemplatePair

    isSecondCombination = actualPairOptions.secondCombination === redirectPair
  }

  const supportedPair = actualPairOptions ? actualPairOptions.supportedPair : false

  return { redirectPair: supportedPair, shouldChangePage, isSecondCombination }
}

const createErrorTag = (): void => {
  let $errorTag = document.querySelector<HTMLParagraphElement>('p.currency-rate-calculator__error')
  const $calcContainer = document.querySelector<HTMLElement>('.currency-rate-calculator__container')

  if (!$errorTag && !!$calcContainer) {
    $errorTag = document.createElement('p')
    $errorTag.classList.add('currency-rate-calculator__error')
    $errorTag.textContent = t9n('formValidation.serverUnknownError')
    $calcContainer.after($errorTag)
  }
}

const changeCurrencyPairTemplate = async (): Promise<void> => {
  sharedElementsHolder.isRedirectPairExist = true
  createErrorTag()
  showError(false)
  const $select1 = document.querySelector<HTMLOptionElement>('[data-tail-select="tail-1"]').value
  const $select2 = document.querySelector<HTMLOptionElement>('[data-tail-select="tail-2"]').value

  const urlCurrencies = `${SINGLE_RATE_BASE_CURRENCY}-${SINGLE_RATE_COUNTER_CURRENCY}`.toLowerCase()
  const urlElements = document.URL.split(urlCurrencies)
  const redirectPair = `${$select1.toLowerCase()}-${$select2.toLowerCase()}`

  const supportedPair = await getSupportedPair(redirectPair, urlCurrencies)
  setLocalStorage('shouldSwapCurrencies', supportedPair.isSecondCombination)
  if (supportedPair.redirectPair) {
    supportedPair.shouldChangePage ? (window.location.href = `${urlElements[0]}${supportedPair.redirectPair}/`) : ''
  } else {
    sharedElementsHolder.isRedirectPairExist = false
    showError(true)
  }
}

const initSelectService = (): void => {
  const $singleSelectOption = document.querySelectorAll<HTMLElement>('[data-key]')
  $singleSelectOption.forEach((option) => {
    option.addEventListener('click', changeCurrencyPairTemplate)
  })
}

const initHelpTip = (): void => {
  const $signHolder = document.querySelector<HTMLSpanElement>('.currency-rate-calculator__sign-holder')
  const $infoPopup = document.querySelector<HTMLSpanElement>('.currency-rate-calculator__answer-info')

  serviceInfoPopup($signHolder, $infoPopup)
}

const initRateCalculator = (): void => {
  initInputs()
  initTailSelect()
  initSelectService()
  handleExchangeButton()
  if ($rateSellInput && $rateBuyInput) {
    userSettingStorage['shouldSwapCurrencies'] ? (swapSelects(), swapCurrencies(false)) : ''
    getValueFromInput()
    rotateIconService()
    getActualInputCurrency()
    initHelpTip()
  }
}

export default initRateCalculator
